import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AuthPageLayout, AuthForm, AuthSuccessLayout } from '../../components/layout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { validateEmail, validatePassword } from '../../utils/validation';
import { ResetPasswordRequest } from '../../services/auth';
import { pageStyles } from '../../config/theme';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { resetPassword, isLoading, error, clearError } = useAuth();
  
  const [formData, setFormData] = useState<ResetPasswordRequest>({
    email: '',
    newPassword: '',
  });
  
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState<Partial<ResetPasswordRequest & { confirmPassword: string }>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Clear errors when form data changes
  useEffect(() => {
    if (error) {
      clearError();
    }
    if (Object.keys(formErrors).length > 0) {
      setFormErrors({});
    }
  }, [formData, confirmPassword, error, clearError]);

  const validateForm = (): boolean => {
    const errors: Partial<ResetPasswordRequest & { confirmPassword: string }> = {};

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.newPassword) {
      errors.newPassword = 'Password is required';
    } else if (!validatePassword(formData.newPassword)) {
      errors.newPassword = 'Password must be at least 6 characters';
    }

    // Confirm password validation
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await resetPassword(formData);
      setIsSuccess(true);
    } catch (error) {
      // Error is handled by the useAuth hook and displayed via error state
      console.error('Password reset failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e as any);
    }
  };

  const handleBackToLogin = () => {
    navigate('/auth/login');
  };

  if (isSuccess) {
    return (
      <AuthSuccessLayout
        title="Password Reset Successful"
        subtitle="Your password has been successfully reset. You can now sign in with your new password."
      >
        <Button
          onClick={handleBackToLogin}
          variant="glass-primary"
          size="lg"
          fullWidth
        >
          Back to Sign In
        </Button>
      </AuthSuccessLayout>
    );
  }

  return (
    <AuthPageLayout
      title="Reset Password"
      subtitle="Enter your email and new password to reset your account"
    >
      <AuthForm onSubmit={handleSubmit}>
            {/* Email Input */}
            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              {...(formErrors.email && { error: formErrors.email })}
              placeholder="Enter your email"
              fullWidth
              autoComplete="email"
              autoFocus
              disabled={isSubmitting || isLoading}
              className="bg-white/90"
            />

            {/* New Password Input */}
            <Input
              label="New Password"
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              {...(formErrors.newPassword && { error: formErrors.newPassword })}
              placeholder="Enter your new password"
              fullWidth
              autoComplete="new-password"
              disabled={isSubmitting || isLoading}
              className="bg-white/90"
            />

            {/* Confirm Password Input */}
            <Input
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              {...(formErrors.confirmPassword && { error: formErrors.confirmPassword })}
              placeholder="Confirm your new password"
              fullWidth
              autoComplete="new-password"
              disabled={isSubmitting || isLoading}
              className="bg-white/90"
            />

            {/* Error Display */}
            {error && (
              <div style={pageStyles.error}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Password Reset Failed
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error.message}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="glass-primary"
              size="lg"
              fullWidth
              loading={isSubmitting || isLoading}
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? 'Resetting Password...' : 'Reset Password'}
            </Button>

            {/* Links */}
            <div className="text-center">
              <Link
                to="/auth/login"
                style={pageStyles.link.primary}
                className="text-sm font-medium"
              >
                Back to Sign In
              </Link>
            </div>
      </AuthForm>
    </AuthPageLayout>
  );
};

export default ResetPasswordPage;
